import {
  VIEWER_PLUGIN_VERSION,
  type ViewerError,
  type ViewerMode,
  type ViewerState,
} from '@genesisframeworks/viewer-plugin';
import { GenesisViewer } from '@genesisframeworks/viewer-plugin/react';
import { Alert, Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { markEnd, markStart } from '@/observability';
import type { EmbedShareInfo } from '@/types/embed.types';
import { ViewerToolbar } from './ViewerToolbar';
import { EmbedFooter } from './components/EmbedFooter';
import { ShareLinkButton } from './components/ShareLinkButton';
import { createDataSource, type ViewerAuth } from './dataSource';

export type { EmbedShareInfo };

export interface ViewerPanelProps {
  auth: ViewerAuth;
  folderPath?: string;
  filePath?: string;
  title: string;
  contentType: 'image' | 'pdf';
  initialMode?: ViewerMode;
  initialState?: Partial<ViewerState>;
  onStateChange?: (state: ViewerState) => void;
  onClose?: () => void;
  getEmbedInfo?: (mode: ViewerMode, state: ViewerState) => Promise<EmbedShareInfo>;
}

function defaultMode(contentType: 'image' | 'pdf'): ViewerMode {
  return contentType === 'image' ? 'image-gallery' : 'pdf-library';
}

export function ViewerPanel({
  auth,
  folderPath,
  filePath,
  title,
  contentType,
  initialMode,
  initialState,
  onStateChange,
  onClose,
  getEmbedInfo,
}: ViewerPanelProps) {
  const [mode, setMode] = useState<ViewerMode>(initialMode || defaultMode(contentType));
  const [viewerState, setViewerState] = useState<ViewerState>({
    mode: initialMode || defaultMode(contentType),
    ...initialState,
  });
  const [error, setError] = useState<ViewerError | null>(null);
  const paintMarked = useRef(false);
  const authKind = auth.kind;
  const publicShareId = auth.kind === 'public' ? auth.shareId : '';

  const dataSource = useMemo(
    () => createDataSource({
      auth: authKind === 'public'
        ? { kind: 'public', shareId: publicShareId }
        : { kind: 'owner' },
      folderPath,
      filePath,
    }),
    [authKind, publicShareId, folderPath, filePath],
  );

  useEffect(() => {
    const next = initialMode || defaultMode(contentType);
    setMode(next);
    setViewerState({ mode: next, ...initialState });
    setError(null);
  }, [contentType, initialMode, initialState, folderPath, filePath, authKind, publicShareId]);

  useEffect(() => {
    markStart('viewer.click-to-paint');
  }, []);

  const handleStateChange = (state: ViewerState) => {
    setViewerState(state);
    if (state.mode !== mode) setMode(state.mode);
    onStateChange?.(state);
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#202124',
        color: 'rgba(255,255,255,0.95)',
      }}
      role="region"
      aria-label="Viewer"
      data-viewer-plugin-version={VIEWER_PLUGIN_VERSION}
    >
      <ViewerToolbar
        title={title}
        contentType={contentType}
        currentMode={mode}
        onModeChange={(next) => {
          setMode(next);
          setViewerState((current) => ({ ...current, mode: next }));
        }}
        onClose={onClose}
        rightSlot={getEmbedInfo
          ? <ShareLinkButton getInfo={() => getEmbedInfo(mode, viewerState)} />
          : undefined}
      />

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ borderRadius: 0, flexShrink: 0 }}
        >
          {error.message}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <GenesisViewer
          dataSource={dataSource}
          mode={mode}
          theme="dark"
          assetBaseUrl="/viewer-plugin-assets/0.2.2/"
          initialState={initialState}
          onReady={(state) => {
            if (!paintMarked.current) {
              paintMarked.current = true;
              markEnd('viewer.click-to-paint', {
                authKind: auth.kind,
                contentType,
                mode: state.mode,
              });
            }
          }}
          onStateChange={handleStateChange}
          onError={setError}
        />
      </Box>

      {getEmbedInfo && (
        <EmbedFooter getEmbedInfo={() => getEmbedInfo(mode, viewerState)} />
      )}
    </Box>
  );
}
